import type { IconName } from "@/components/ui/icon";

import { COPY } from "./copy";

export type Tone = "neutral" | "info" | "success" | "warning" | "danger";

/**
 * Closed state set — mirrors backend `ConversationState` (models/conversations/enums.py).
 * The OpenAPI schema types `state` as an open string, so the keyed Records below are the
 * compile-time guard that every state has a label (decision 156: one text label per state).
 */
export const CONVERSATION_STATES = [
  "scheduled", "joining", "await_member", "await_consent", "capturing", "paused",
  "consent_hold", "member_hold", "processing", "ready", "skipped", "blocked",
  "declined", "cancelled", "failed", "empty", "no_show", "discarded", "revoked",
  "consent_withdrawn",
] as const;
export type ConversationState = (typeof CONVERSATION_STATES)[number];

export const TRANSCRIPT_PENDING_STATES = new Set<string>([
  "scheduled",
  "joining",
  "await_member",
  "await_consent",
  "capturing",
  "paused",
  "consent_hold",
  "member_hold",
  "processing",
]);

/** The six attention groups of spec 02 §2.13. */
export const ATTENTION_GROUPS = [
  "unresolved", "job_no_candidate", "multi_candidate_untriaged",
  "unassigned_speakers", "scorecard_type_unselected", "unhandled_replies",
] as const;
export type AttentionGroup = (typeof ATTENTION_GROUPS)[number];

export interface StateLabel { label: string; icon: IconName; tone: Tone }

export const STATE_LABELS: Record<ConversationState, StateLabel> = {
  scheduled: { label: "Scheduled", icon: "IconCalendar1", tone: "neutral" },
  joining: { label: "Joining", icon: "IconLoader", tone: "info" },
  await_member: { label: "Waiting for a member", icon: "IconClock", tone: "info" },
  await_consent: { label: "Waiting for consent", icon: "IconShield", tone: "info" },
  capturing: { label: "Recording", icon: "IconRecord", tone: "danger" },
  paused: { label: "Paused", icon: "IconPause", tone: "warning" },
  consent_hold: { label: "On hold (consent)", icon: "IconShield", tone: "warning" },
  member_hold: { label: "On hold (no member)", icon: "IconClock", tone: "warning" },
  processing: { label: "Processing", icon: "IconLoader", tone: "info" },
  ready: { label: "Ready", icon: "IconCheckmark1", tone: "success" },
  skipped: { label: "Skipped", icon: "IconSkip", tone: "neutral" },
  blocked: { label: "Blocked", icon: "IconCircleBanSign", tone: "danger" },
  declined: { label: "Declined", icon: "IconCircleBanSign", tone: "neutral" },
  cancelled: { label: "Cancelled", icon: "IconCircleX", tone: "neutral" },
  failed: { label: "Failed", icon: "IconExclamationCircle", tone: "danger" },
  empty: { label: "Empty", icon: "IconMinusSmall", tone: "neutral" },
  no_show: { label: "No-show", icon: "IconUserBlock", tone: "neutral" },
  discarded: { label: "Discarded", icon: "IconTrashCanSimple", tone: "neutral" },
  revoked: { label: "Revoked", icon: "IconShieldBreak", tone: "warning" },
  consent_withdrawn: { label: "Consent withdrawn", icon: "IconShieldBreak", tone: "warning" },
};

export const ATTENTION_LABELS: Record<AttentionGroup, string> = {
  unresolved: "Unmatched",
  job_no_candidate: "Unmatched",
  multi_candidate_untriaged: "Group interview to triage",
  unassigned_speakers: "Unassigned speakers",
  scorecard_type_unselected: "Scorecard type to pick",
  unhandled_replies: "Reply to handle",
};

/** Hover copy for the Needs attention column. The badge stays short; this says what to do. */
export const ATTENTION_HINTS: Record<AttentionGroup, string> = {
  unresolved: "This meeting isn't linked to a job.",
  job_no_candidate: "This meeting is linked to a job, but not to a candidate.",
  multi_candidate_untriaged: "Several candidates were in this meeting. Assign them under Speakers.",
  unassigned_speakers: "A speaker in the transcript still needs a person or a role.",
  scorecard_type_unselected: "Pick which scorecard this meeting should use.",
  unhandled_replies: "Someone replied to the recording notice and it hasn't been handled.",
};

const UNKNOWN_STATE_ICON: IconName = "IconMinusSmall";

/** Safe accessor for the open `string` the API returns: never crash on a state added later. */
export function stateLabel(state: string): StateLabel {
  return (
    (STATE_LABELS as Record<string, StateLabel | undefined>)[state] ??
    { label: state, icon: UNKNOWN_STATE_ICON, tone: "neutral" }
  );
}

/** Safe accessor for attention groups; falls back to the raw code. */
export function attentionLabel(group: string): string {
  return (ATTENTION_LABELS as Record<string, string | undefined>)[group] ?? group;
}

export function attentionHint(group: string): string {
  return (ATTENTION_HINTS as Record<string, string | undefined>)[group] ?? attentionLabel(group);
}

/** Platform meeting-type keys seeded in 02 (`seed_meeting_types`). */
export const MEETING_TYPES = [
  "candidate_interview",
  "role_intake",
  "sourcing_intake",
  "client_intake",
  "panel_debrief",
  "reference_check",
  "pipeline_review",
  "internal_other",
] as const;
export type MeetingType = (typeof MEETING_TYPES)[number];

export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  candidate_interview: "Candidate interview",
  role_intake: "Role intake",
  sourcing_intake: "Sourcing intake",
  client_intake: "Client intake",
  panel_debrief: "Panel debrief",
  reference_check: "Reference check",
  pipeline_review: "Pipeline review",
  internal_other: "Internal / other",
};

function humaniseKey(key: string): string {
  const spaced = key.replace(/_/g, " ");
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key;
}

/** Presentation-only humanisers for the open enums that have no label map. */
export function captureModeLabel(key: string | null | undefined): string {
  return key ? humaniseKey(key) : COPY.sources.none;
}

export function dataClassLabel(key: string | null | undefined): string {
  return key ? humaniseKey(key) : COPY.sources.none;
}

/** Safe accessor for the open `string` the API returns. */
export function meetingTypeLabel(key: string): string {
  return (
    (MEETING_TYPE_LABELS as Record<string, string | undefined>)[key] ??
    humaniseKey(key)
  );
}

/** state_reason / block_reason / JoinDecision.reason code → label (TDD §3.1 reason catalog). 03d wrote the first nine; 04e adds the bot path (spec 04 §4.9, §7.3). */
export const REASON_LABELS: Record<string, string> = {
  unassigned_speakers: "Speakers still need to be assigned",
  unresolved: "Not linked to a job yet",
  asr_failed: "Transcription failed",
  media_unreadable: "The recording could not be read",
  media_scratch_full: "Processing is backed up; we will retry",
  media_unavailable: "The recording never arrived from the meeting bot",   // 04 widens 03d's wording: bot path (§4.6 recording_done_wait) and media stage share the code
  content_hash_mismatch: "The uploaded file did not match its declared hash",
  transcript_cap: "Transcription limit reached",
  internal: "Processing failed; we will look into it",   // 03d (d20b1c6d): a processing run failed on an internal DB step — unchanged
  // ── spec 04 §7.3 join ladder ──
  feature_disabled: "Meeting capture is not on your plan",
  channel_disabled: "Calendar capture is turned off for your organization",
  org_bot_limit: "Your organization's bot limit was reached",
  participant_cap: "Too many attendees for a bot",
  user_override: "Skipped by request",
  internal_only_unresolved: "Internal meeting with no job match",
  user_default: "External meeting; the owner's calendar join is off",
  standing_decline: "A candidate has declined recording",
  event_cancelled: "The calendar event was cancelled",
  moved_after_dispatch: "The event moved after the bot was sent",
  // ── spec 04 §4.9 bot outcomes ──
  lobby_timeout: "Nobody admitted the bot from the lobby",
  no_in_call_events: "The bot never reported joining",
  start_recording_failed: "The bot could not start recording",
  recording_permission_denied: "The host did not allow recording",
  sign_in_failed: "The meeting required a sign-in the bot does not have",
  login_capacity: "No signed-in bot was available",
  meeting_url_invalid: "The meeting link was invalid or expired",
  meeting_not_accessible: "The meeting was locked, full or password-protected",
  meeting_full: "The meeting was full",
  launch_late: "The bot launched too late to join",
  // spec 06 §4.3: refusals moved to `bot_blocked`; `platform_error` is now the honest bucket for
  // "the platform or the bot provider failed and nobody can do anything about it".
  platform_error: "Something went wrong at the meeting platform",
  recall_pool_exhausted: "No bot was free in time",
  consent_declined: "A participant declined recording",
  too_short: "Shorter than the minimum duration",
  // ── Thread.block_reason members 03d did not label (enums.py BlockReason; 04c writes the first two, 02's binding service the last two) ──
  multi_candidate_untriaged: "Several candidates were in this meeting — assign them under Speakers first",
  below_threshold: "The job match was not confident enough",
  job_archived: "The linked job was archived",
  candidate_deleted: "The linked candidate was removed",
  // ── 04b `map_sub_code` / `_terminal` reasons on `empty` / `failed` / `cancelled` rows (spec §4.5, §4.9) ──
  // PermanentRecallError body codes not listed here (conversation_missing, calendar_adapter_unavailable, Recall's own 4xx codes) take the humanised fallback deliberately
  no_member: "No member of your organization joined",
  nobody: "Nobody joined the meeting",
  everyone_left: "Everyone left the meeting",
  silence: "The meeting went silent",
  call_ended: "The meeting ended",
  only_bots: "Only bots were in the meeting",
  meeting_ended: "The meeting had already ended",
  recall_timer: "The bot's own timer ended the call",
  session_ceiling: "The session limit was reached",
  ejected: "The bot was removed from the meeting",
  admin: "Cancelled from Arbi",                              // also the → processing reason when cancel("admin") arrives mid-recording (04b _on_cancel) — true on both rows
  duplicate_bot: "Another Arbi bot was already in the meeting",
  calendar_disconnected: "The calendar was disconnected",
  recall_conflict: "The bot provider reported a conflicting bot",
  no_event_links: "The calendar event was no longer linked",
  no_meeting_url: "The event has no supported meeting link",
  // ── `→ processing` reasons (chip tooltip only; the row is `processing` / `ready`) ──
  stop: "Stopped by a member",
  leave: "The bot was asked to leave",
  overrun: "The scheduled end passed",
  max_session: "The session limit was reached",
  empty_room: "Everyone left the meeting",
  // ── spec 05 §2.4 state_reason / §6.2 cut_kind / consent sources rendered as reasons ──
  consent_pending: "Waiting for a participant's consent",
  consent_missing: "Consent was not given",
  // consent_declined: 04e already labels it ("A participant declined recording")
  grace: "Recording starts after a short grace period",
  revoked: "A participant revoked recording",
  erasure: "Removed at the person's request",
  member_absent: "No member of your organization is in the meeting",   // 06's HoldReason; labelled now so `paused(member_absent)` never humanises
  // admin: 04e labels it "Cancelled from Arbi" (cancelled rows); on `paused` the same code means "Paused by an admin" — see REASON_LABELS_BY_STATE
  // ── spec 06 §4.3 platform limits — seven new reason codes over Recall's 43 documented fatals ──
  platform_unrecognized: "Unrecognized platform error",                      // §4.4: "we do not know", not "the platform refused"
  sign_in_required: "The meeting only admits signed-in participants",
  sign_in_not_configured: "The bot has no sign-in set up for this platform",
  bot_blocked: "The meeting platform would not let the bot in",
  meeting_type_unsupported: "This kind of meeting cannot be recorded",
  meeting_not_started: "The meeting had not started",                        // §4.4: corrected from `meeting_ended`, on both arms
  unsupported_platform: "Arbi cannot record meetings on this platform",      // §4.7: Zoom for Government, Teams GCC High / DoD
};

export function reasonLabel(code: string | null | undefined): string {
  if (!code) return "";
  return REASON_LABELS[code] ?? humaniseKey(code);
}

/**
 * Mirrors backend `PartialReason` (`models/conversations/enums.py`) after `cv06` widened
 * `ck_conv_conversations_partial_reason` to seven members (spec 06 §2.1, decision 226).
 * `partial_reason` had no renderer anywhere in the app before 06b.
 */
export const PARTIAL_REASONS = [
  "late_join", "ejected", "members_left", "revoked", "consent_hold", "no_show", "platform_failure",
] as const;
export type PartialReason = (typeof PARTIAL_REASONS)[number];

export const PARTIAL_REASON_LABELS: Record<PartialReason, string> = {
  late_join: "The bot joined after the meeting had started",
  ejected: REASON_LABELS.ejected,                     // "The bot was removed from the meeting" — same event, same words
  members_left: "Everyone from your organization left before the end",
  revoked: REASON_LABELS.revoked,                     // "A participant revoked recording"
  consent_hold: "Recording was held while consent was outstanding",
  no_show: "Nobody joined for part of the meeting",
  // spec 06 §4.4 / decision 226: roughly forty sub-codes moved here from `ejected`, which had
  // been telling the user the bot was kicked out of a meeting it was never removed from.
  platform_failure: "The meeting platform ended the capture",
};

/** Open `string` from the API: an unknown partial reason renders nothing rather than a raw code. */
export function partialReasonLabel(code: string | null | undefined): string {
  if (!code) return "";
  return (PARTIAL_REASON_LABELS as Record<string, string | undefined>)[code] ?? "";
}

/**
 * CAP-32's "setup-guide entries" half (spec 06 §6), keyed by Recall's raw `sub_code`, which
 * reaches the row through `metrics.platform_sub_code` → `ConversationOut.platform_sub_code`
 * (§4.6). **Exactly the seventeen §4.2 rows whose `actor_code` is `host` or `workspace_admin`.**
 * A row with any other actor deliberately has no entry and renders the reason label alone:
 * nobody in the product can act on it, and inventing an instruction would be worse than silence.
 *
 * `actor_code = workspace_admin` is the customer's **Google Workspace / Microsoft 365 tenant
 * admin** (§4.3) — not an Arbi org admin, who is the person reading this line.
 */
export const SUBCODE_GUIDANCE: Record<string, string> = {
  // ── Google Meet ──
  google_meet_bot_blocked: "The host blocked the bot from this Meet call. Ask them to admit Arbi when it asks to join.",
  google_meet_knocking_disabled: "This Meet call does not let guests ask to be admitted. Ask the host to turn on Quick access in the meeting's host controls.",
  google_meet_watermark_kicked: "Google removed the bot because this meeting has watermarking turned on. Ask the host to turn watermarking off for meetings you want recorded.",
  google_meet_meeting_room_not_ready: "The Meet room was not open yet. Ask the host to start the meeting at the scheduled time, or record it again once it is running.",
  google_meet_organisation_restricted: "The host's Google Workspace does not allow participants from outside the organization. Ask whoever administers that Google Workspace to allow external participants in Meet.",
  // ── Microsoft Teams ──
  microsoft_teams_bot_not_invited: "Teams would not admit the bot. Ask the host to allow anonymous users to join this meeting, or to admit Arbi from the lobby.",
  microsoft_teams_event_not_started_for_external: "The host had not started the Teams meeting, so people outside the organization could not join. Ask them to start it before the scheduled time.",
  // ── Shared / generic ──
  meeting_not_found: "The link did not resolve to a meeting. Check the meeting link on the calendar event and update it.",
  meeting_link_invalid: "The meeting link was not valid. Check the link on the calendar event and update it.",
  meeting_link_expired: "The meeting link had expired. Put a current link on the calendar event.",
  meeting_not_accessible: "The bot could not get into the meeting. Ask the host to admit it from the lobby, or to use a join link with the passcode already in it.",
  meeting_password_incorrect: "The passcode was missing or wrong. Ask the host to use a join link with the passcode already in it.",
  meeting_locked: "The host had locked the meeting. Ask them to unlock it, or to admit the bot before locking it.",
  meeting_full: "The meeting was at its participant limit. Ask the host to free a seat or raise the limit before the next one.",
  meeting_requires_registration: "This meeting requires registration, which the bot cannot complete. Ask the host to send a direct join link instead.",
  meeting_requires_sign_in: "This meeting only admits signed-in participants. Ask the host to allow guests, or share the recording another way.",
  meeting_not_started: "The meeting had not started when the bot tried to join. Ask the host to start it at the scheduled time, or record it again once it is running.",
};

/**
 * The two reasons that say, in words, that there is nothing to change on this side (spec §6).
 * Everything else with no `SUBCODE_GUIDANCE` row renders the reason label and stops.
 */
export const NO_ACTION_REASONS: Record<string, string> = {
  platform_error: "Nothing on your side caused this — the meeting platform or the bot provider failed. If it keeps happening, contact support.",
  platform_unrecognized: "The meeting platform returned an error we do not recognize yet. We have recorded the details for support.",
};

/** Guidance for a failed capture: the sub-code's line if the actor can act, else the no-action sentence, else nothing. */
export function failureGuidance(
  stateReason: string | null | undefined,
  subCode: string | null | undefined,
): string | null {
  if (subCode && SUBCODE_GUIDANCE[subCode]) return SUBCODE_GUIDANCE[subCode];
  if (stateReason && NO_ACTION_REASONS[stateReason]) return NO_ACTION_REASONS[stateReason];
  return null;
}

export const SCOPE_LABELS = COPY.share.scope;

export const GRANTEE_LEVEL_LABELS: Record<string, string> = {
  job: COPY.share.job,
  job_candidate: COPY.share.candidate,
  project: "Project",
};

/** Mirrors backend `SourceKind` (models/conversations/enums.py). `fromMap` humanises anything added later. */
export const SOURCE_KIND_LABELS: Record<string, string> = {
  recording: "Recording",
  upload: "Upload",
  document: "Document",
  chat: "Chat",
  screen_share: "Screen share",
  human_notes: "Notes",
  imported_transcript: "Imported transcript",
};

/** Mirrors backend `SourceRole`; 03c writes `secondary` on every document Source (spec §10.37). */
export const SOURCE_ROLE_LABELS: Record<string, string> = {
  canonical: "Canonical",
  secondary: "Secondary",
  continuation: "Continuation",
};

/**
 * Mirrors backend `DiarizationQuality`. `speaker_timeline` arrived with cv13, when
 * `write_transcript_rows` stopped stamping the literal `machine` on every transcript:
 * Recall falls back to it wherever the meeting platform has no separate audio streams.
 */
export const DIARIZATION_LABELS: Record<string, string> = {
  perfect: "Perfect",
  hybrid: "Hybrid",
  speaker_timeline: "Speaker timeline",
  machine: "Machine",
  none: "None",
};

/** Mirrors backend `Platform`. */
export const PLATFORM_LABELS: Record<string, string> = {
  zoom: "Zoom",
  meet: "Google Meet",
  teams: "Microsoft Teams",
  webex: "Webex",
};

/** Mirrors backend `AsrProvider`. The column stores the vendor key; nobody reads `assemblyai`. */
export const ASR_PROVIDER_LABELS: Record<string, string> = {
  recall: "Recall",
  deepgram: "Deepgram",
  assemblyai: "AssemblyAI",
};

/**
 * Kinds the Sources tab lists as a quiet footnote rather than as cards.
 *
 * Neither is a recording. Both are JSON blobs the bot path derives from platform
 * events (`build_side_sources`), stored in `s3_key_transcript` because it is the only
 * text column on a Source — so `has_transcript` is true for them and means nothing.
 * Neither has media, so `GET /media-url` answers 404 `MEDIA_NOT_AVAILABLE` for both.
 */
export const SIDE_SOURCE_KINDS: readonly string[] = ["chat", "screen_share"];

/**
 * The kinds `GET /{id}/markers` can return, not the whole `SegmentMarker` enum.
 *
 * Narrowed rather than synced. The map had drifted both ways — it carried
 * `chapter`, which is not an enum member at all, and omitted `class_change` and
 * `room_change`, which are — but syncing it would have reinstated six members
 * that no writer produces. `screen_share` is the only span kind the endpoint
 * serves; `cut` never arrives as a marker either, only as the scalar
 * `truncated_at_ms`. The reserved point lane's `kind` is open, so anything it
 * one day carries falls through `markerLabel` to `humaniseKey`.
 */
export const MARKER_LABELS: Record<string, string> = {
  screen_share: "Screen share",
};

/** Roles a participant can be given from the UI (spec 03 §3.4 step 2; `unknown` is the server-side placeholder). */
export const IMPORT_ROLES = ["interviewer", "candidate", "client", "reference", "other"] as const;
export type ImportRole = (typeof IMPORT_ROLES)[number];

/** Narrows the open `string` a select hands back to the closed role set (no cast at the call site). */
export function isImportRole(value: string): value is ImportRole {
  return (IMPORT_ROLES as readonly string[]).includes(value);
}

export const ROLE_LABELS: Record<string, string> = {
  interviewer: "Interviewer",
  candidate: "Candidate",
  client: "Client",
  reference: "Reference",
  other: "Other",
  unknown: "Unassigned",
};

export function roleLabel(role: string | null | undefined): string {
  return fromMap(ROLE_LABELS, role);
}

/** Only these Source kinds may be detached (spec 03 §6.2 `DELETE /{id}/sources/{source_id}`). */
export const DETACHABLE_KINDS: readonly string[] = ["human_notes", "document"];

export function formatTalkTime(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * States in which `POST /{id}/capture/command` is accepted (spec 04 §4.7 + spec 05 §4 "consent_hold joins LIVE_STATES").
 * `scheduled` is refused with 409 CAPTURE_NOT_LIVE, so the header hides Stop / Leave there. `consent_hold` is
 * commandable — Stop / Leave must work during a hold (spec 05 §4, blocker 9) — but never resumable (`isResumable`).
 */
export const CAPTURE_COMMAND_STATES = ["joining", "await_member", "await_consent", "capturing", "paused", "consent_hold"] as const;

export function isCaptureCommandable(state: string): boolean {
  return (CAPTURE_COMMAND_STATES as readonly string[]).includes(state);
}

/** One code, two states (spec 05 §3.7 / §4 vs spec 04 §4.5): `admin` on `paused` is the UI hold; on `cancelled` / `processing` it is 04's cancel. */
export const REASON_LABELS_BY_STATE: Partial<Record<ConversationState, Record<string, string>>> = {
  paused: { admin: "Paused by an admin" },
  consent_withdrawn: { revoked: "Consent was withdrawn after the meeting" },   // 05a's finish_cut writes state_reason = revoked on both terminals
};

/** `reasonLabel` with the state-specific overlay consulted first. */
export function reasonLabelFor(state: string, code: string | null | undefined): string {
  if (!code) return "";
  return (REASON_LABELS_BY_STATE as Record<string, Record<string, string> | undefined>)[state]?.[code] ?? reasonLabel(code);
}

/** UI `command = pause` is offered while recording (spec 05 §4 "capturing → paused(admin)"). */
export function isHoldable(state: string): boolean {
  return state === "capturing";
}

/** UI `command = resume` is offered on `paused` only — never on `consent_hold` (spec 05 §9; the route answers 409 CAPTURE_NOT_LIVE). */
export function isResumable(state: string): boolean {
  return state === "paused";
}

/** The Resume label differs by `state_reason` (spec 05 §9): an admin hold resumes; a member-absent hold resumes "anyway". */
export function resumeLabel(stateReason: string | null | undefined): string {
  return stateReason === "member_absent" ? COPY.capture.holdResume.resumeAnyway : COPY.capture.holdResume.resume;
}

/** `POST /{id}/revoke` acts on "a live or ended capture" (spec 05 §3.7): every commandable state plus the two that hold a recording. */
export const REVOCABLE_STATES = [...CAPTURE_COMMAND_STATES, "processing", "ready"] as const;

export function isRevocable(state: string): boolean {
  return (REVOCABLE_STATES as readonly string[]).includes(state);
}

/** Mirrors backend `ConsentState` (`Participant.consent_state`) and `ParticipantStanding.standing_code` (spec 05 §3.1). */
export const CONSENT_STANDINGS = ["granted", "declined", "withdrawn", "pending", "not_required", "not_collected"] as const;
export type ConsentStanding = (typeof CONSENT_STANDINGS)[number];

export const CONSENT_STANDING_LABELS: Record<ConsentStanding, string> = {
  granted: "Consented",
  declined: "Declined",
  withdrawn: "Withdrawn",
  pending: "Pending",
  not_required: "Not required",
  not_collected: "Not collected",
};

export function consentStandingLabel(code: string): string {
  return (CONSENT_STANDING_LABELS as Record<string, string | undefined>)[code] ?? humaniseKey(code);
}

/** Mirrors backend `ConsentRank`. `policy` is a basis-of-processing record and is never rendered as consent (decision 155e). */
export const RANK_LABELS: Record<"own" | "on_behalf" | "policy", string> = {
  own: "Their own choice",
  on_behalf: "Recorded on their behalf",
  policy: "No objection",
};

/** Mirrors backend `ConsentSource` after cv05 (spec 05 §2.1: + notice_no_objection, reply_override, erasure, ui_self). */
export const CONSENT_SOURCE_LABELS: Record<string, string> = {
  email_link: "Notice link",
  in_call_chat: "Meeting chat",
  in_call_dtmf: "Phone keypad",
  ats: "ATS",
  interviewer_override: "Interviewer in the meeting",
  desktop_confirm: "Desktop app",
  ui_on_behalf: "Recorded in Arbi",
  policy_default: "Organization default",
  notice_no_objection: "Notice sent, no objection",
  reply_override: "Email reply, recorded in Arbi",
  erasure: "Erasure request",
  ui_self: "Withdrawn in Arbi",
};

/** `JoinDecisionKind` → the ConversationState whose `StateChip` renders it on the Schedule tab (spec 04 §9). */
export const DECISION_STATES: Record<string, ConversationState> = {
  join: "scheduled",
  skip: "skipped",
  blocked: "blocked",
  declined: "declined",
  cancelled: "cancelled",
};

export function decisionState(decision: string): string {
  return DECISION_STATES[decision] ?? decision;
}

function fromMap(map: Record<string, string>, key: string | null | undefined): string {
  if (!key) return COPY.sources.none;
  return map[key] ?? humaniseKey(key);
}

export function scopeLabel(key: string): string {
  return (SCOPE_LABELS as Record<string, string>)[key] ?? humaniseKey(key);
}

export function granteeLevelLabel(key: string | null | undefined): string {
  return fromMap(GRANTEE_LEVEL_LABELS, key);
}

export function sourceKindLabel(key: string): string {
  return fromMap(SOURCE_KIND_LABELS, key);
}

export function sourceRoleLabel(key: string): string {
  return fromMap(SOURCE_ROLE_LABELS, key);
}

export function diarizationLabel(key: string): string {
  return fromMap(DIARIZATION_LABELS, key);
}

export function platformLabel(key: string | null | undefined): string {
  return fromMap(PLATFORM_LABELS, key);
}

export function asrProviderLabel(key: string): string {
  return fromMap(ASR_PROVIDER_LABELS, key);
}

export function markerLabel(key: string): string {
  return fromMap(MARKER_LABELS, key);
}

export function bindingLevelLabel(key: string): string {
  return fromMap(GRANTEE_LEVEL_LABELS, key);
}
